import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { Sparkles, Globe, Eye, MessageSquare, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const SharedConversationPage = () => {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchConversation = async () => {
      try {
        const res = await axios.get(`${API}/lore/shared/${shareId}`);
        setData(res.data);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchConversation();
  }, [shareId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)] flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center pt-20">
          <div className="text-center">
            <MessageSquare className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white font-['Outfit'] mb-2">Conversation Not Found</h1>
            <p className="text-slate-400 text-sm mb-6">This shared conversation may have expired or doesn't exist.</p>
            <Link to="/lore-explorer">
              <Button className="btn-primary">Start Your Own Conversation</Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const firstQuestion = data.messages?.find(m => m.role === "user")?.content || "";
  const firstAnswer = data.messages?.find(m => m.role === "assistant")?.content || "";

  return (
    <div className="min-h-screen bg-[var(--bg-base)] flex flex-col">
      <SEOHead
        title={`${data.universe_name} Lore — Shared Conversation`}
        description={firstAnswer.slice(0, 160)}
      />
      <Navbar />
      <main className="flex-1 pt-20 pb-8 px-4 max-w-3xl mx-auto w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-500/20 bg-amber-500/5 mb-3">
            <Sparkles className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-xs font-mono tracking-wider text-amber-500 uppercase">Shared Lore Conversation</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] mb-2">{data.universe_name}</h1>
          <div className="flex items-center justify-center gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1"><Globe className="w-3 h-3" /> {data.universe_name}</span>
            <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {data.views || 0} views</span>
            <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {data.messages?.length || 0} messages</span>
          </div>
        </div>

        {/* Messages */}
        <div className="space-y-4 mb-8" data-testid="shared-messages">
          {data.messages?.map((msg, i) => (
            <div
              key={i}
              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              data-testid={`shared-msg-${i}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                  msg.role === "user"
                    ? "bg-amber-500/15 border border-amber-500/20 text-white"
                    : "bg-white/[0.04] border border-white/5 text-slate-300"
                }`}
              >
                {msg.role === "assistant" && (
                  <div className="flex items-center gap-1.5 mb-2">
                    <Sparkles className="w-3 h-3 text-amber-500" />
                    <span className="text-[10px] font-mono tracking-wider text-amber-500 uppercase">Lore Oracle</span>
                  </div>
                )}
                <p className="text-sm leading-relaxed whitespace-pre-line">{msg.content}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center card-surface p-6">
          <h3 className="text-white font-['Outfit'] font-semibold mb-2">Want to explore more?</h3>
          <p className="text-slate-400 text-sm mb-4">Ask the Lore Oracle your own questions about {data.universe_name} and beyond.</p>
          <Link to={`/lore-explorer?universe=${encodeURIComponent(data.universe_name)}`}>
            <Button className="btn-primary" data-testid="start-own-conversation">
              Start Your Own Conversation <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SharedConversationPage;
